import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api.js';

const EMPTY = {
  companyName: '',
  tin: '',
  businessAddress: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  billingAddress: '',
  notifyEmail: true,
  notifySms: false,
};

/**
 * Public customer self-registration portal (UC a809cc41). Anonymous shipping
 * lines / agents submit corporate + billing details; the request is saved with
 * status "Pending Admin Approval" and an administrator is notified.
 */
export default function CustomerRegistrationPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // the created registration

  // Clear the field's error (and any form-level error) as soon as the user edits it.
  const set = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((er) => {
      if (!er[field] && !er.form) return er;
      const next = { ...er };
      delete next[field];
      delete next.form;
      return next;
    });
  };

  const validate = () => {
    const next = {};
    if (!form.companyName.trim() || form.companyName.trim().length < 3)
      next.companyName = 'Company name is required (min 3 characters)';
    if (!form.tin.trim()) next.tin = 'TIN is required';
    else if (!/^[A-Za-z0-9-]+$/.test(form.tin.trim())) next.tin = 'TIN must be alphanumeric';
    if (!form.businessAddress.trim()) next.businessAddress = 'Business address is required';
    if (!form.contactName.trim()) next.contactName = 'Contact name is required';
    if (!form.contactEmail.trim()) next.contactEmail = 'Contact email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail)) next.contactEmail = 'Enter a valid email';
    if (!form.contactPhone.trim()) next.contactPhone = 'Contact phone is required';
    if (!form.billingAddress.trim()) next.billingAddress = 'Billing address is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrors((er) => ({ ...er, form: undefined }));
    if (!validate()) return;
    setSubmitting(true);
    try {
      const created = await api.registerCustomer({
        companyName: form.companyName.trim(),
        tin: form.tin.trim(),
        businessAddress: form.businessAddress.trim(),
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim(),
        contactPhone: form.contactPhone.trim(),
        billingAddress: form.billingAddress.trim(),
        notifyEmail: form.notifyEmail,
        notifySms: form.notifySms,
      });
      setSubmitted(created);
    } catch (err) {
      setErrors((er) => ({ ...er, form: err.message || 'Registration failed. Please try again.' }));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container py-5" data-testid="registration-success">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card border-0 shadow-sm text-center">
              <div className="card-body p-5">
                <i className="fas fa-circle-check fa-3x text-success mb-3" aria-hidden="true" />
                <h2 className="h4 mb-2">Registration submitted</h2>
                <p className="text-muted mb-3">
                  Thank you, <strong>{submitted.company_name}</strong>. Your application has been
                  received.
                </p>
                <span className="badge bg-warning text-dark p-2 mb-4" data-testid="registration-status">
                  Status: {submitted.status}
                </span>
                <p className="small text-muted mb-4">
                  An administrator has been notified and will review your account. You’ll be able to
                  sign in once your account is approved.
                </p>
                <Link to="/login" className="btn btn-primary">
                  Back to sign in
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4" data-testid="customer-registration-page">
      <div className="row justify-content-center">
        <div className="col-lg-9">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm mb-4">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-ship me-2" aria-hidden="true" />
                TugOS Customer Portal
              </h3>
              <p className="text-muted mb-0 small">
                Register your shipping line or maritime agency account.
              </p>
            </div>
            <span className="badge bg-primary p-2">Port Security Compliant</span>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom-0 pt-4">
              <h5 className="mb-0 text-secondary">
                <i className="fas fa-file-invoice me-2" aria-hidden="true" />
                Registration Application
              </h5>
            </div>
            <div className="card-body">
              {errors.form && (
                <div className="alert alert-danger" role="alert" data-testid="registration-error">
                  {errors.form}
                </div>
              )}
              <form onSubmit={submit} noValidate>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="companyName" className="form-label small fw-bold">
                      Company / Agency Name <span className="text-danger">*</span>
                    </label>
                    <input
                      id="companyName"
                      className={`form-control${errors.companyName ? ' is-invalid' : ''}`}
                      value={form.companyName}
                      onChange={(e) => set('companyName', e.target.value)}
                      data-testid="company-name"
                    />
                    {errors.companyName && <div className="invalid-feedback">{errors.companyName}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="tin" className="form-label small fw-bold">
                      Tax Identification Number (TIN) <span className="text-danger">*</span>
                    </label>
                    <input
                      id="tin"
                      className={`form-control${errors.tin ? ' is-invalid' : ''}`}
                      value={form.tin}
                      onChange={(e) => set('tin', e.target.value)}
                      data-testid="tin"
                    />
                    {errors.tin && <div className="invalid-feedback">{errors.tin}</div>}
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="businessAddress" className="form-label small fw-bold">
                    Registered Business Address <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="businessAddress"
                    rows={2}
                    className={`form-control${errors.businessAddress ? ' is-invalid' : ''}`}
                    value={form.businessAddress}
                    onChange={(e) => set('businessAddress', e.target.value)}
                    data-testid="business-address"
                  />
                  {errors.businessAddress && <div className="invalid-feedback">{errors.businessAddress}</div>}
                </div>

                <hr className="my-3" />
                <h6 className="text-muted fw-bold mb-3 small text-uppercase">
                  <i className="fas fa-address-book me-1" aria-hidden="true" />
                  Primary Contact &amp; Billing Information
                </h6>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label htmlFor="contactName" className="form-label small fw-bold">
                      Contact Name <span className="text-danger">*</span>
                    </label>
                    <input
                      id="contactName"
                      className={`form-control${errors.contactName ? ' is-invalid' : ''}`}
                      value={form.contactName}
                      onChange={(e) => set('contactName', e.target.value)}
                      data-testid="contact-name"
                    />
                    {errors.contactName && <div className="invalid-feedback">{errors.contactName}</div>}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="contactEmail" className="form-label small fw-bold">
                      Contact Email <span className="text-danger">*</span>
                    </label>
                    <input
                      id="contactEmail"
                      type="email"
                      className={`form-control${errors.contactEmail ? ' is-invalid' : ''}`}
                      value={form.contactEmail}
                      onChange={(e) => set('contactEmail', e.target.value)}
                      data-testid="contact-email"
                    />
                    {errors.contactEmail && <div className="invalid-feedback">{errors.contactEmail}</div>}
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="contactPhone" className="form-label small fw-bold">
                      Contact Phone <span className="text-danger">*</span>
                    </label>
                    <input
                      id="contactPhone"
                      className={`form-control${errors.contactPhone ? ' is-invalid' : ''}`}
                      value={form.contactPhone}
                      onChange={(e) => set('contactPhone', e.target.value)}
                      data-testid="contact-phone"
                    />
                    {errors.contactPhone && <div className="invalid-feedback">{errors.contactPhone}</div>}
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="billingAddress" className="form-label small fw-bold">
                    Billing Address <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="billingAddress"
                    rows={2}
                    className={`form-control${errors.billingAddress ? ' is-invalid' : ''}`}
                    value={form.billingAddress}
                    onChange={(e) => set('billingAddress', e.target.value)}
                    data-testid="billing-address"
                  />
                  {errors.billingAddress && <div className="invalid-feedback">{errors.billingAddress}</div>}
                </div>

                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="notifyEmail"
                    checked={form.notifyEmail}
                    onChange={(e) => set('notifyEmail', e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="notifyEmail">
                    Receive booking &amp; account notifications by email
                  </label>
                </div>
                <div className="form-check mb-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="notifySms"
                    checked={form.notifySms}
                    onChange={(e) => set('notifySms', e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="notifySms">
                    Receive SMS alerts
                  </label>
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                    data-testid="submit-registration"
                  >
                    {submitting && (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    )}
                    <i className="fas fa-paper-plane me-1" aria-hidden="true" />
                    Submit Registration
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/login')}
                    data-testid="cancel-registration"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
